import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getUserProfileAction } from "../../../redux/slices/users/usersSlice";
import CustomerDetails from "./CustomerDetails";
import Skeleton from "../../Skeleton/Skeleton";

export default function CustomerProfile() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedShipping, setExpandedShipping] = useState({});
  
  useEffect(() => {
    dispatch(getUserProfileAction());
  }, [dispatch]);
  
  // Simple YYYY-MM-DD format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Format time with 24-hour format
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  // Format date and time together
  const formatDateTime = (dateString) => {
    return `${formatDate(dateString)} ${formatTime(dateString)}`;
  };
  
  // Get status badge color based on order status
  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'PENDIENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMADO':
        return 'bg-blue-100 text-blue-800';
      case 'ENVIADO':
        return 'bg-purple-100 text-purple-800';
      case 'ENTREGADO':
        return 'bg-green-100 text-green-800';
      case 'CANCELADO':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get translated status text
  const getTranslatedStatus = (status) => {
    switch(status) {
      case 'PENDIENTE':
        return t('profile.pending');
      case 'CONFIRMADO':
        return t('profile.confirmed');
      case 'ENVIADO':
        return t('profile.shipped');
      case 'ENTREGADO':
        return t('profile.delivered');
      case 'CANCELADO':
        return t('profile.cancelled');
      default:
        return status || t('profile.pending');
    }
  };
  
  //get data from store
  const { error, loading, profile } = useSelector((state) => state?.users);
  
  const user = profile;
  const orders = user?.orders || [];
  const defaultShippingAddress = user?.shippingAddress;
  
  // Toggle order expansion
  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };
  
  // Toggle shipping address visibility for a specific order
  const toggleShippingAddress = (orderId) => {
    setExpandedShipping(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };
  
  // Show loading state
  if (loading) {
  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Page title */}
        <div className="mb-8">
          <Skeleton className="h-8 w-48" />
        </div>

        {/* Customer Details card skeleton */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <Skeleton className="h-5 w-40 mb-4" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-5 w-48" />
            </div>
            <div>
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-5 w-36" />
            </div>
            <div>
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-5 w-32" />
            </div>
          </div>
        </div>

        {/* Orders section title */}
        <div className="mt-10 mb-6">
          <Skeleton className="h-8 w-40" />
        </div>

        {/* Order cards */}
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white shadow overflow-hidden sm:rounded-lg">
              {/* Order header (collapsed state) */}
              <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-center">
                  <div>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <Skeleton className="h-4 w-20 mb-1" />
                      <Skeleton className="h-6 w-20 rounded-full" />
                    </div>
                    {/* Arrow icon placeholder */}
                    <Skeleton className="h-5 w-5 rounded" />
                  </div>
                </div>
              </div>
              {/* No inner details shown (collapsed state) */}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
  
  // Show error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-2">⚠️</div>
          <h2 className="text-lg text-red-600">{t('profile.errorLoadingProfile')}</h2>
          <p className="text-gray-500 mt-2">{error?.message || t('common.error')}</p>
        </div>
      </div>
    );
  }
  
  // Show message if no user data
  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <h2 className="text-lg text-gray-600">{t('profile.noUserDataFound')}</h2>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.myProfile')}</h1>
        </div>
        
        {/* Customer Details Section */}
        <div className="mb-8">
          <CustomerDetails
            email={user?.email}
            dateJoined={formatDate(user?.createdAt)}
            fullName={user?.fullname}
          />
        </div>

        {/* Orders Section */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.myOrders')}</h2>
          
          {orders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                ></path>
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">{t('profile.noOrdersFound')}</h3>
              <p className="mt-1 text-gray-500">{t('profile.noOrdersMessage')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order._id} className="bg-white shadow overflow-hidden sm:rounded-lg">
                  {/* Order Header - Always Visible */}
                  <div 
                    className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleOrder(order._id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">{t('profile.orderNumber')}</p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">{order?.orderNumber || order?._id}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">{t('profile.datePlaced')}</p>
                          <div className="mt-1">
                            <p className="text-sm text-gray-900">{formatDateTime(order?.createdAt)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">{t('profile.totalAmount')}</p>
                          <p className="mt-1 text-lg font-bold text-gray-900">${order?.totalPrice}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">{t('profile.orderStatus')}</p>
                          <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(order?.status)}`}>
                            {getTranslatedStatus(order?.status)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Expand/Collapse Icon */}
                      <div className="ml-4 flex-shrink-0">
                        <svg
                          className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${expandedOrders[order._id] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Details - Collapsible Content */}
                  {expandedOrders[order._id] && (
                    <div className="px-4 py-5 sm:p-6">
                      {/* Order Items */}
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('profile.orderItems')}</h3>
                      <div className="space-y-4">
                        {order?.orderItems?.map((product, idx) => (
                          <div
                            key={product._id || idx}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-gray-200 last:border-0"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0 h-20 w-20">
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="h-full w-full object-cover rounded-lg"
                                />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  {t('profile.size')}: {product.size || t('profile.default')} | {t('profile.style')}: {product.style || t('profile.standard')}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  ${product.price} x {product.qty} = ${product.totalPrice}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 sm:mt-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                order?.paymentStatus === "PAGADO" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                              }`}>
                                {order?.paymentStatus === "PAGADO" ? t('profile.paid') : t('profile.pendingPayment')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Order Summary */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-500">{t('profile.subtotal')}:</span>
                          <span className="text-gray-900">${order?.totalPrice}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="font-medium text-gray-500">{t('profile.shipping')}:</span>
                          <span className="text-gray-900">$0.00</span>
                        </div>
                        <div className="flex justify-between text-base font-medium mt-2 pt-2 border-t border-gray-200">
                          <span>{t('profile.total')}:</span>
                          <span className="text-orange-600">${order?.totalPrice}</span>
                        </div>
                      </div>
                      
                      {/* Order Time Details */}
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs text-gray-500">{t('profile.orderPlacedAt')}</p>
                            <p className="text-sm text-gray-900">{formatDateTime(order?.createdAt)}</p>
                          </div>
                          {order?.updatedAt && order.updatedAt !== order.createdAt && (
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.lastUpdated')}</p>
                              <p className="text-sm text-gray-900">{formatDateTime(order?.updatedAt)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Toggle Shipping Address Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleShippingAddress(order._id);
                        }}
                        className="mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center"
                      >
                        {expandedShipping[order._id] ? (
                          <>
                            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                            </svg>
                            {t('profile.hideShippingAddress')}
                          </>
                        ) : (
                          <>
                            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                            {t('profile.showShippingAddress')}
                          </>
                        )}
                      </button>
                      
                      {/* Shipping Address for this Order - Collapsible */}
                      {expandedShipping[order._id] && order.shippingAddress && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('profile.shippingAddressUsed')}</h4>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.fullName')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.nombre} {order.shippingAddress.paterno}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.phone')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.telefono}</p>
                            </div>
                            <div className="sm:col-span-2">
                              <p className="text-xs text-gray-500">{t('profile.address')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.direccion}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.neighborhood')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.colonia}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.municipality')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.delegacion}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.postalCode')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.zipcode}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('profile.state')}</p>
                              <p className="text-sm text-gray-900">{order.shippingAddress.estado}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Default Shipping Address Section */}
        {defaultShippingAddress && Object.keys(defaultShippingAddress).length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.defaultShippingAddress')}</h2>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.fullName')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.nombre} {defaultShippingAddress.paterno}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.phone')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.telefono}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm font-medium text-gray-500">{t('profile.address')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.direccion}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.neighborhood')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.colonia}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.municipality')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.delegacion}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.postalCode')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.zipcode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('profile.state')}</p>
                    <p className="mt-1 text-sm text-gray-900">{defaultShippingAddress.estado}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}